import { queryOptions } from '@tanstack/react-query';
import type { DietSettings } from '@diet/types';
import { fetchEntries } from '@diet/data/diet-repository';
import { fetchSettings } from '@diet/data/diet-settings-repository';

/*
 * 查詢定義的單一來源。
 *
 * 每個查詢是一支回傳 `queryOptions()` 的函式，而不是散落的 key 常數加上呼叫端自己
 * 帶的 queryFn —— 這是 TanStack Query v5 的建議寫法。`queryOptions` 在執行期只是把
 * 傳入的東西原封不動回傳，價值全在型別與「key 與 fn 綁在一起」：
 *
 * - **同一把 key 不會配到不同的 queryFn。** 兩者分開放時，寫入端與查詢端各自組 key，
 *   差一個字就是「按了沒反應」——不會報錯，只是畫面不更新。
 * - **key 不必手抄。** `queryClient.setQueryData(entriesOptions().queryKey, …)` 的
 *   型別由這裡推導出來，不需要在呼叫端再標一次泛型。
 * - **同一份定義餵得進所有入口**：`useQuery`、`useQueries`、`setQueryData`、
 *   之後若要在路由層 prefetch 也是同一支，不必把 key 與 fn 再寫一次。
 *
 * 兩把 key 都掛在 `'diet'` 底下，之後若需要一次失效整個領域可以直接用這個前綴。
 * （目前不需要：session 失效時 `QueryProvider` 是整份 `clear()`。）
 */

/**
 * 近 90 天的紀錄。
 *
 * 區間由 repository 依「今天」推算，故不進 key —— 否則跨過午夜就會多出一把
 * 永遠不會再被讀到的快取。
 */
export function entriesOptions() {
  return queryOptions({
    queryKey: ['diet', 'entries'] as const,
    queryFn: fetchEntries,
  });
}

/** 取不到設定時的退路，等同於「什麼都沒設定」。 */
export const NO_SETTINGS: DietSettings = { dailyCalorieGoal: null };

/**
 * 每日目標等設定。
 *
 * 失敗時降級成 `NO_SETTINGS` 而不是讓查詢進入錯誤狀態：沒有紀錄就沒有畫面可看，
 * 但沒有設定只是少了達成度顯示。降級寫在 queryFn 而非 UI，這樣「設定讀不到」永遠
 * 等於「沒有設定」，不會有某個畫面忘記處理 undefined 而顯示 NaN。真正需要處理的
 * 錯誤（session 過期）會由同時進行的紀錄查詢一起浮現，不會被這個 catch 藏住。
 */
export function settingsOptions() {
  return queryOptions({
    queryKey: ['diet', 'settings'] as const,
    queryFn: () => fetchSettings().catch(() => NO_SETTINGS),
  });
}
